import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "./auth";
import api from "./api";

/**
 * Where to send a freshly-loaded user based on their role + verification state.
 * Used by both `/` and the catch-all `*` routes.
 */
export function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-[#163144]">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "doctor") {
    if (user.email_verified === false)
      return <Navigate to="/verifica-tu-correo" replace />;
    if (!user.specialty)
      return <Navigate to="/elegir-especialidad" replace />;
    return <Navigate to="/doctor" replace />;
  }
  if (user.role === "secretary") return <Navigate to="/secretaria" replace />;
  if (user.role === "pharmacy") return <Navigate to="/farmacia" replace />;
  return <Navigate to="/login" replace />;
}

/** Login + role guard for any protected route. */
export function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-[#163144]">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role))
    return <Navigate to="/" replace />;
  return children;
}

/** Forces doctors through email verification + specialty selection. */
export function RequireVerifiedEmail({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "doctor" && user.email_verified === false) {
    return <Navigate to="/verifica-tu-correo" replace />;
  }
  if (user.role === "doctor" && !user.specialty) {
    return <Navigate to="/elegir-especialidad" replace />;
  }
  return children;
}

/**
 * Doctor-only gate that also forces the mandatory onboarding flow (creating
 * the first office + secretary). Rebounds to /onboarding if the doctor still
 * has zero offices.
 */
export function DoctorOnboardingGate({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ checking: true, needs: false });

  useEffect(() => {
    if (user?.role !== "doctor") {
      // eslint-disable-next-line
      setState({ checking: false, needs: false });
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.get("/offices");
        if (!cancelled) {
          // eslint-disable-next-line
          setState({ checking: false, needs: !r.data || r.data.length === 0 });
        }
      } catch {
        if (!cancelled) {
          // eslint-disable-next-line
          setState({ checking: false, needs: false });
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (user?.role === "doctor" && user.email_verified === false) {
    return <Navigate to="/verifica-tu-correo" replace />;
  }
  if (user?.role === "doctor" && !user.specialty) {
    return <Navigate to="/elegir-especialidad" replace />;
  }
  if (state.checking) return <div className="p-10 text-black">Cargando…</div>;
  if (state.needs) return <Navigate to="/onboarding" replace />;
  return children;
}
